import {Col, Layout, Menu, Row, Space} from 'antd';
import {AssetLocationCard} from '../components/asset/cards/AssetLocationCard';
import {AssetDetailCard} from '../components/asset/cards/AssetDetailCard';
import {RealEstateDataCard} from '../components/asset/cards/RealEstateDataCard';
import IdentifiedHazardsCard from '../components/asset/cards/hazards/IdentifiedHazardsCard';
import {AssetContext} from '../context/assetDetails/AssetContext';
import {
  FileOutlined,
  GlobalOutlined,
  HeatMapOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {capitalize} from 'lodash';
import {useTranslation} from 'react-i18next';
import {useMemo, useState} from 'react';
import {AssetSummaryCard} from '../components/common/AssetSummaryCard';
import {DocumentsListCard} from '../components/asset/cards/DocumentsListCard';
import {ImagesListCard} from '../components/asset/cards/ImagesListCard';
import PermissionRoute from '../components/Authentication/PermissionRoute';
import {
  isAlertParametersView,
  isAssetDocumentView,
  isAssetImageView,
  isAssetUogView,
  isBiodiversityView,
  isClimateChangeView
} from '../components/Authentication/permissions';
import {CustomSider} from '../components/common/CustomSider';
import {AssetEventsHistoryCard} from '../components/asset/cards/AssetEventsHistoryCard';
import usePreviousPath from '../customHooks/usePreviousPath';
import ClimateChangeCard from '../components/asset/cards/climateChange/ClimateChangeCard';
import ClimateChangeIcon from '../components/common/icons/ClimateChangeIcon';
import UOGTable from '../components/asset/uog/UOGTable';
import BiodiversityNatureCard from "../components/asset/cards/biodiversityNature/BiodiversityNatureCard";
import AlertParametersCard from "../components/asset/cards/alertParameters/AlertParametersCard";
import {useGetUserProfileQuery} from "../redux/api/locales";

const {Content} = Layout;
export default function Asset() {
  
  const {t} = useTranslation();

  const {data: userData} = useGetUserProfileQuery();
  
  const [
    currentStep,
    setCurrentStep,
  ] = useState('buildingInformation');
  
  const items = useMemo(() => ([
    {
      key: 'buildingInformation',
      label: capitalize(t('assets.assetInformation')),
      icon: <InfoCircleOutlined/>,
    },
    {
      key: 'eventHistoryAndNotes',
      label: capitalize(t('events.historyEvent')),
      icon: <HeatMapOutlined/>,
    },
    {
      key: 'identifiedHazards',
      label: capitalize(t('hazards.risksAndHazards')),
      icon: <RiseOutlined/>,
    },
    ...userData?.type && isBiodiversityView(userData.type) ? [{
      key: 'biodiversityNature',
      label: capitalize(t('assets.biodiversityNature')),
      icon: <GlobalOutlined/>,
    }] : [],
    ...userData?.type && isAlertParametersView(userData.type) ? [{
      key: 'alertParameters',
      label: capitalize(t('assets.alertParameters')),
      icon: <WarningOutlined/>,
    }] : [],
    ...process.env.REACT_APP_SHOW_CLIMATE_CHANGE === 'show' && userData?.type && isClimateChangeView(userData.type) ? [{
      key: 'climateChange',
      label: capitalize(t('assets.climateChange')),
      icon: <ClimateChangeIcon/>,
    }] : [],
    {
      key: 'documents',
      label: capitalize(t('documents.documentsAndImages')),
      icon: <FileOutlined/>,
    },
    ...userData?.type && isAssetUogView(userData.type) ? [{
      key: 'staff',
      label: capitalize(t('contacts.staff')),
      icon: <TeamOutlined/>,
    }] : [],
  ]), [t, userData?.type]);
  
  const {path, label} = usePreviousPath();
  const navigationProps = path && label ?
    {backButtonLabel: capitalize(t('actions.goBackDestination', {destination: label})), destination: path} :
    {backButtonLabel: capitalize(t('misc.goBackToAssets')), destination: '/assets'};
  
  return (
    <>
      <AssetContext>
        <Layout style={{position: 'relative'}}>
          <CustomSider
            {...navigationProps}
            title={capitalize(t('assets.assetDetail'))}
            content={
              <Menu
                theme="dark"
                defaultSelectedKeys={['buildingInformation']}
                onSelect={(item) => setCurrentStep(item.key)}
                mode="inline"
                items={items}
              />
            }
            summaryCard={<AssetSummaryCard/>}
          />
          <Content style={{overflowY: 'scroll'}}>
            <div style={{margin: '1.5rem 1.5rem'}}>
              <Row gutter={[28, 28]}>
                <Col span={24}>
                  <Space
                    direction={'vertical'}
                    style={{display: 'flex'}}
                    size={'large'}
                  >
                    {currentStep === 'buildingInformation' && (
                      <>
                        <AssetLocationCard/>
                        <AssetDetailCard/>
                        <RealEstateDataCard/>
                      </>
                    
                    )}
                    {currentStep === 'identifiedHazards' && (
                      <>
                        <IdentifiedHazardsCard/>
                      </>
                    )}
                    {currentStep === 'eventHistoryAndNotes' && (
                      <>
                        <AssetEventsHistoryCard/>
                      </>
                    )}
                     {currentStep === 'biodiversityNature' && (
                      <PermissionRoute
                          isAuthorized={isBiodiversityView}
                        >
                        <BiodiversityNatureCard />
                      </PermissionRoute>
                    )}
                    {currentStep === 'alertParameters' && (
                      <PermissionRoute isAuthorized={isAlertParametersView}>
                        <AlertParametersCard/>
                      </PermissionRoute>
                    )}
                    {currentStep === 'climateChange' && process.env.REACT_APP_SHOW_CLIMATE_CHANGE === 'show' && (
                      <PermissionRoute
                          isAuthorized={isClimateChangeView}
                        >
                        <ClimateChangeCard/>
                      </PermissionRoute>
                    )}
                    {currentStep === 'documents' && (
                      <>
                        <PermissionRoute
                          isAuthorized={isAssetDocumentView}
                        >
                          <DocumentsListCard/>
                        </PermissionRoute>
                        <PermissionRoute
                          isAuthorized={isAssetImageView}
                        >
                          <ImagesListCard/>
                        </PermissionRoute>
                      </>
                    )}
                    {currentStep === 'staff' && (
                      <>
                        <UOGTable/>
                      </>
                    )}
                  </Space>
                </Col>
              </Row>
            </div>
          </Content>
        </Layout>
      </AssetContext>
    </>
  );
}